/**
 * @fileoverview Composable for audio recording functionality
 * @module composables/useAudioRecording
 *
 * Manages audio recording state and controls for
 * voice message input in the chat window.
 *
 * @vue3migration - Phase 3E: Extracted from ChatWindow/Index.vue
 */
import { ref, type Ref, type ShallowRef } from 'vue'
import type { ComponentPublicInstance } from 'vue'

/**
 * Audio recorder component interface
 */
interface AudioRecorderComponent extends ComponentPublicInstance {
  startRecording: () => void
  cancelAudio: () => void
}

/**
 * Options for useAudioRecording composable
 */
interface UseAudioRecordingOptions {
  /** Ref to chatbox text value (cleared when recording starts) */
  chatbox: Ref<string>
  /** Template ref to audio recorder component */
  audioRecorderRef: Readonly<ShallowRef<AudioRecorderComponent | null>>
}

/**
 * Composable for audio recording functionality
 *
 * @param options - Dependencies and refs
 * @returns Audio recording state and control methods
 *
 * @example
 * ```ts
 * const {
 *   useAudioInput,
 *   audioFile,
 *   sendingAudio,
 *   openRecording,
 *   updateAudioFile,
 *   micBackAction,
 * } = useAudioRecording({
 *   chatbox,
 *   audioRecorderRef: audioRecorder,
 * })
 * ```
 */
export function useAudioRecording(options: UseAudioRecordingOptions) {
  const { chatbox, audioRecorderRef } = options

  // ====================
  // State
  // ====================

  /** Whether audio input mode is active */
  const useAudioInput = ref(false)

  /** Current audio file blob from recording */
  const audioFile = ref<Blob | null>(null)

  /** Whether audio is currently being sent/uploaded */
  const sendingAudio = ref(false)

  // ====================
  // Methods
  // ====================

  /**
   * Open audio recording mode
   * Clears chatbox and starts recording via nextTick
   */
  function openRecording() {
    useAudioInput.value = true
    chatbox.value = ''
    // Note: Component should call startRecording in nextTick
    // using audioRecorderRef.value?.startRecording()
  }

  /**
   * Start recording after nextTick (called by component)
   */
  function startRecordingNextTick() {
    audioRecorderRef.value?.startRecording()
  }

  /**
   * Update the audio file from recording
   *
   * @param file - Recorded audio blob
   */
  function updateAudioFile(file: Blob | null) {
    audioFile.value = file
  }

  /**
   * Handle mic back button action
   * Cancels recording if file exists, otherwise closes audio input
   */
  function micBackAction() {
    if (audioFile.value) {
      audioRecorderRef.value?.cancelAudio()
    } else {
      useAudioInput.value = false
    }
  }

  /**
   * Cancel audio recording and reset state
   */
  function cancelAudio() {
    audioRecorderRef.value?.cancelAudio()
  }

  /**
   * Reset audio state after sending
   */
  function resetAfterSend() {
    sendingAudio.value = false
    audioRecorderRef.value?.cancelAudio()
  }

  /**
   * Set sending state
   *
   * @param value - Whether audio is being sent
   */
  function setSendingAudio(value: boolean) {
    sendingAudio.value = value
  }

  return {
    // State
    useAudioInput,
    audioFile,
    sendingAudio,

    // Methods
    openRecording,
    startRecordingNextTick,
    updateAudioFile,
    micBackAction,
    cancelAudio,
    resetAfterSend,
    setSendingAudio,
  }
}
